/** @jsxImportSource @emotion/react */
import { useState, useEffect } from "react";
import { Dropdown } from "primereact/dropdown";
import { Message } from "primereact/message";
import { Boton, accessAPI } from "../../../shared";

/**
 * Prueba un script de numeración antes de que empiece a nombrar registros.
 *
 * Corre contra datos reales del organismo —el tipo de certificado que se elija,
 * con su tipo de proceso, su norma y su esquema— y arma los datos con el mismo
 * código que la generación real, así lo que se ve acá es lo que va a salir.
 *
 * Dos cosas lo diferencian de la ejecución real, a propósito:
 *  - no consume numeración, los contadores se proyectan sin incrementarse, así
 *    que se puede probar todas las veces que haga falta;
 *  - los errores se muestran en vez de caer a la numeración por defecto, que es
 *    lo que hace la plataforma en producción para no frenar un alta.
 *
 * Recibe el texto del editor, no lo guardado: la idea es probar antes de guardar.
 */
export default function SimuladorNumeracion({ tipo, codigo }) {
  const [tiposCertificado, setTiposCertificado] = useState([]);
  const [certificadoTipoId, setCertificadoTipoId] = useState(null);
  const [resultado, setResultado] = useState(null);
  const [probando, setProbando] = useState(false);

  useEffect(() => {
    accessAPI(
      "GET",
      "admin/config/certificadotipo",
      null,
      (respuesta) => {
        const opciones = (respuesta || []).map((t) => ({
          value: t.value,
          label: t.etiqueta
        }));
        setTiposCertificado(opciones);
        if (opciones.length > 0) setCertificadoTipoId(opciones[0].value);
      },
      () => setTiposCertificado([])
    );
  }, []);

  // El resultado deja de valer en cuanto se toca el script.
  useEffect(() => setResultado(null), [codigo, tipo]);

  function probar() {
    setProbando(true);
    setResultado(null);
    accessAPI(
      "POST",
      "admin/config/numeracion/simular",
      { tipo, codigo: codigo ?? "", certificadoTipoId },
      (respuesta) => {
        setResultado(respuesta);
        setProbando(false);
      },
      (respuesta) => {
        setResultado({
          ok: false,
          error: respuesta?.[0]?.msg || "No se pudo probar el script"
        });
        setProbando(false);
      }
    );
  }

  const contadores = Object.entries(resultado?.contadores || {});

  return (
    <div>
      <div css={{ display: "flex", gap: "0.5em", alignItems: "center", flexWrap: "wrap" }}>
        <Dropdown
          value={certificadoTipoId}
          options={tiposCertificado}
          onChange={(e) => setCertificadoTipoId(e.value)}
          placeholder="Tipo de certificado"
          filter
          css={{ flex: "1 1 16rem" }}
        />
        <Boton callback={probar} loader={probando} disabled={!certificadoTipoId}>
          Probar
        </Boton>
      </div>

      {resultado?.ok && (
        <div css={{ marginTop: "1em" }}>
          <div css={{ fontSize: "0.85em", color: "var(--grisTextos)" }}>
            {resultado.sinScript
              ? "Sin script, se usa la numeración por defecto"
              : "Con este script quedaría"}
          </div>
          <div css={{ fontSize: "1.4em", fontWeight: 600, wordBreak: "break-all" }}>
            {resultado.nombre}
          </div>
          {contadores.length > 0 && (
            <div css={{ marginTop: "0.5em", fontSize: "0.85em", color: "var(--grisTextos)" }}>
              Tomaría {contadores.map(([c, v]) => `${c} → ${v}`).join(", ")}. La
              prueba no consume numeración.
            </div>
          )}
        </div>
      )}

      {resultado && !resultado.ok && (
        <div css={{ marginTop: "1em" }}>
          <Message
            severity="error"
            css={{ width: "100%", justifyContent: "flex-start", textAlign: "left" }}
            text={
              (resultado.error || "El script falló") +
              (resultado.linea ? ` (línea ${resultado.linea})` : "")
            }
          />
          {resultado.porDefecto && (
            <div css={{ marginTop: "0.5em", fontSize: "0.85em", color: "var(--grisTextos)" }}>
              Así como está, la plataforma usaría la numeración por defecto:{" "}
              <strong>{resultado.porDefecto}</strong>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
